/**
 * Centrally-controlled interstitial pacing gate.
 *
 * Pure state machine: the orchestrator owns the policy fetch (`/ads/init`)
 * and the side effects (event emission, callbacks); this class only answers
 * "would showing right now violate the limits?" and tracks the rolling
 * one-hour window.
 *
 * Why client-side: pacing is a UX guardrail, not anti-fraud. Nobody is
 * incentivised to cheat their own ads-per-minute budget. Server-side
 * enforcement would add a blocking round-trip before every interstitial show
 * for no defensive benefit.
 *
 * Why a class with injectable `now`: makes the unit tests deterministic
 * without monkey-patching `Date.now`.
 */
const HOUR_MS = 60 * 60 * 1000;
export class PacingGate {
    constructor() {
        this.config = null;
        this.sessionStartAt = 0;
        this.lastShowAt = null;
        this.recentShows = [];
    }
    /**
     * Apply pacing config. Pass `null` to disable pacing for the session — all
     * subsequent `evaluate()` calls return `null` (allowed). Always called on
     * SDK init; safe to call again on re-init.
     */
    configure(config, now = Date.now()) {
        this.config = config;
        this.sessionStartAt = now;
        this.lastShowAt = null;
        this.recentShows = [];
    }
    /** True only when a non-null config is active. */
    isActive() {
        return this.config !== null;
    }
    /**
     * Determine whether an interstitial show would be allowed right now.
     * Returns `null` when allowed, or the suppression reason when blocked.
     * Pure — does not mutate state. Caller must call `recordShow()` separately
     * after a successful render.
     */
    evaluate(now = Date.now()) {
        const cfg = this.config;
        if (!cfg)
            return null;
        if (now - this.sessionStartAt < cfg.warmupMs) {
            return 'warmup';
        }
        if (this.lastShowAt !== null && now - this.lastShowAt < cfg.minIntervalMs) {
            return 'min_interval';
        }
        const recentCount = this.countRecentShows(now);
        if (recentCount >= cfg.maxPerHour) {
            return 'hourly_cap';
        }
        return null;
    }
    /**
     * Record a successful interstitial show. No-op when pacing is inactive —
     * the call sites don't have to branch on config presence.
     */
    recordShow(now = Date.now()) {
        if (!this.config)
            return;
        this.lastShowAt = now;
        this.recentShows.push(now);
        this.pruneOldShows(now);
    }
    countRecentShows(now) {
        this.pruneOldShows(now);
        return this.recentShows.length;
    }
    pruneOldShows(now) {
        if (this.recentShows.length === 0)
            return;
        const cutoff = now - HOUR_MS;
        // Timestamps are appended in order so the prune is a single shift loop.
        let drop = 0;
        while (drop < this.recentShows.length && this.recentShows[drop] < cutoff) {
            drop++;
        }
        if (drop > 0) {
            this.recentShows = this.recentShows.slice(drop);
        }
    }
}
